// Pure helpers of the guided workout (/execute): warm-up entries, the how-to as steps, the load step of the
// equipment, and the load a changed set carries to the sets after it

// Warm-up and feeder entries are saved as "Supino (Aquecimento)" / "Supino (Preparo)" (or just flagged warmup)
const SET_KIND_SUFFIX = /\s*\((warm-?up|aquecimento|feeder|preparo)\)\s*$/i;

/** The lift's own name and what the entry is: { base: 'Supino', kind: 'warmup' | 'feeder' | null } */
export const entryKind = (exercise) => {
  const name = exercise?.name || '';
  const tag = name.match(SET_KIND_SUFFIX)?.[1];
  if (!tag && !exercise?.warmup) return { base: name, kind: null };
  return { base: name.replace(SET_KIND_SUFFIX, '').trim(), kind: /feeder|preparo/i.test(tag || '') ? 'feeder' : 'warmup' };
};

/**
 * A how-to as its steps, one per line, without the numbers they're written with ("1. Deite-se no banco.").
 * Shown as one paragraph, the line breaks were lost and the steps ran together. A text with the numbers inline
 * ("1. Deite-se. 2. Segure a barra.") splits at them too.
 */
export const howToSteps = (text) =>
  String(text || '')
    .split(/\r?\n/)
    .flatMap((line) => line.split(/\s+(?=\d{1,2}[.)]\s)/))
    .map((line) => line.replace(/^\s*\d{1,2}\s*[.)]\s*/, '').trim())
    .filter(Boolean);

// What the load moves by on − and +, in the equipment's own jumps: a pair of dumbbells, two small plates on a bar,
// a pin on a stack
const STEP = {
  kg: { dumbbell: 2, kettlebell: 4, barbell: 2.5, machine: 5, plate: 5, other: 2.5 },
  lbs: { dumbbell: 5, kettlebell: 5, barbell: 5, machine: 10, plate: 10, other: 5 },
};

// A workout's own entries (no library id) only have a name to go by
const EQUIPMENT_BY_NAME = [
  [/halter|dumbbell/i, 'dumbbell'],
  [/kettlebell/i, 'kettlebell'],
  [/smith|barra|barbell/i, 'barbell'],
  [/polia|cabo|corda|crossover|m[áa]quina|cadeira|mesa flexora|leg press|pulley|cable|rope|machine/i, 'machine'],
];

export const loadStep = (equipment, name, unit) => {
  const steps = STEP[unit === 'lbs' || unit === 'lb' ? 'lbs' : 'kg'];
  const known = steps[equipment] ? equipment : EQUIPMENT_BY_NAME.find(([pattern]) => pattern.test(name || ''))?.[1];
  return steps[known] || steps.other;
};

/** A load typed in pt ("52,5") as the log stores it ("52.5"): digits and one decimal point */
export const normalizeLoad = (value) => {
  const [whole, ...rest] = String(value ?? '')
    .replace(',', '.')
    .replace(/[^\d.]/g, '')
    .split('.');
  return rest.length ? `${whole}.${rest.join('')}` : whole;
};

/** The load after one step up or down; nothing at zero (the set has no load) */
export const stepLoad = (value, delta) => {
  const next = Math.round(((parseFloat(value) || 0) + delta) * 100) / 100;
  return next > 0 ? String(next) : '';
};

/** "52.5" as read in pt: "52,5" */
export const formatNumber = (value, isPt) => (isPt ? String(value ?? '').replace('.', ',') : String(value ?? ''));

const setKey = (exerciseIdx, setNumber) => `${exerciseIdx}-${setNumber}`;

/**
 * A load changed in a set carries to the later sets of the same exercise that weren't changed by hand
 * (`weightTyped`). It used to carry only when the exercise had no load of its own, so whoever raised the load on
 * set 1 typed it again on every set. Returns the new overrides ({ 'exerciseIdx-setNumber': { reps, weight, ... } }).
 */
export const carryLoad = (overrides, exerciseIdx, setNumber, seriesCount, value) => {
  if (!value) return overrides;
  const updated = { ...overrides };
  for (let s = setNumber + 1; s <= seriesCount; s += 1) {
    const key = setKey(exerciseIdx, s);
    if (!updated[key]?.weightTyped) updated[key] = { ...updated[key], weight: value };
  }
  return updated;
};
